// src/pages/Events.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../services/api";
import SmartImg from "../components/SmartImg";
import ExternalDestinationsSection from "../components/ExternalDestinationsSection";
import EventsEuropeGlobe from "../components/EventsEuropeGlobe";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const stockPill = (remaining, reserved = 0) => {
  if (remaining <= 0)
    return {
      text: reserved > 0 ? "Unavailable" : "Sold out",
      cls: reserved > 0 ? "bg-white/10 text-slate-200" : "bg-red-500/20 text-red-200",
    };
  if (remaining <= 10) return { text: `${remaining} remaining`, cls: "bg-amber-500/20 text-amber-200" };
  return { text: "Available", cls: "bg-emerald-500/20 text-emerald-200" };
};

const PUBLIC_EVENTS_TIMEOUT_MS = 3500;

// Make a Date from separate date + time strings (handles “HH:mm” or “HH:mm:ss”)
const toDate = (dateStr, timeStr) => {
  if (!timeStr) return new Date(dateStr);
  let t = timeStr;
  if (/^\d{2}:\d{2}$/.test(t)) t += ":00";
  if (!/^\d{2}:\d{2}:\d{2}$/.test(t)) t = "00:00:00";
  return new Date(`${dateStr}T${t}`);
};

const isPlanned = (ev) => ev.status === "PLANNED";
const badgeFor = (ev) => {
  if (isPlanned(ev)) return { label: "Coming soon", cls: "bg-violet-500/20 text-violet-200" };
  if (ev.cancelled) return { label: "Cancelled", cls: "bg-red-500/20 text-red-200" };
  const remaining = (ev.remainingStandardTickets || 0) + (ev.remainingVipTickets || 0);
  const reserved = ev.reservedVipTickets || 0;
  if (remaining <= 0) {
    return reserved > 0
      ? { label: "Unavailable", cls: "bg-white/10 text-slate-200" }
      : { label: "Sold out", cls: "bg-red-500/20 text-red-200" };
  }
  if (remaining <= 10) return { label: "Last tickets", cls: "bg-amber-500/20 text-amber-200" };
  return null;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [userTickets, setUserTickets] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/events/list", { timeout: PUBLIC_EVENTS_TIMEOUT_MS });
      const now = new Date();

      // Include PLANNED items; exclude cancelled; sort dated first by start, then undated
      const normStart = (ev) => (ev.date ? toDate(ev.date, ev.startTime) : null);
      const upcoming = (data || [])
        .filter((ev) => !ev.cancelled)
        .filter((ev) => {
          const s = normStart(ev);
          return s ? s > now : true; // undated show as “coming soon”
        })
        .sort((a, b) => {
          const sa = normStart(a), sb = normStart(b);
          if (sa && sb) return sa - sb;
          if (sa && !sb) return -1; // dated first
          if (!sa && sb) return 1;
          return 0;
        });

      setEvents(upcoming);
    } catch {
      setEvents([]);
      setErr("");
    } finally {
      setLoading(false);
    }

    // Ticket counts (non-fatal if it fails)
    try {
      const { data } = await api.get("/api/tickets/tickets-per-event", { timeout: PUBLIC_EVENTS_TIMEOUT_MS });
      setUserTickets(data || {});
    } catch { }
  };

  const actionFor = (ev) => {
    if (isPlanned(ev)) {
      return <span className="badge bg-violet-500/20 text-violet-200 md:hidden">Coming soon</span>;
    }
    if (ev.cancelled) {
      return <span className="badge bg-red-500/20 text-red-200">Cancelled</span>;
    }

    const standardRemaining = ev.remainingStandardTickets || 0;
    const vipRemaining = ev.remainingVipTickets || 0;
    const remaining = standardRemaining + vipRemaining;
    const reserved = ev.reservedVipTickets || 0;

    if (remaining <= 0) {
      return (
        <span className={`badge ${reserved > 0 ? "bg-white/10 text-slate-200" : "bg-red-500/20 text-red-200"}`}>
          {reserved > 0 ? "Unavailable" : "Sold out"}
        </span>
      );
    }

    return (
      <button
        onClick={() => navigate(`/purchase/${ev.id}`)}
        className="btn-primary w-full sm:w-auto"
        aria-label={`Purchase tickets for ${ev.title}`}
      >
        Purchase tickets
      </button>
    );
  };

  return (
    <div className="page space-y-6">
      {/* Header */}
      <section className="border border-white/10 bg-black/55 p-6">
        <div className="grid gap-6 lg:grid-cols-[1fr,26rem] lg:items-center">
          <div className="lg:max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-brand-500">HLT events</p>
            <h1 className="mt-2 font-display text-5xl leading-none text-white md:text-6xl">
              Events across Europe
            </h1>
            <p className="mt-3 max-w-3xl text-slate-300">
              Browse Ibiza and Marbella affiliate listings, then switch to London for HLT's
              in-house ticketed events and QR checkout.
            </p>
          </div>
          <EventsEuropeGlobe />
        </div>
      </section>

      <ExternalDestinationsSection />

      <section className="pt-4">
        <p className="text-xs uppercase tracking-[0.28em] text-brand-500">HLT checkout</p>
        <h2 className="mt-2 font-display text-4xl leading-none text-white">In-house tickets</h2>
      </section>

      {/* Loading skeletons */}
      {loading && (
        <section className="grid gap-4 sm:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="grid gap-3 md:grid-cols-[7rem,1fr]">
                <div className="h-40 md:h-28 w-full md:w-28 rounded-xl bg-white/5" />
                <div className="space-y-2">
                  <div className="h-5 w-2/3 bg-white/5 rounded" />
                  <div className="h-4 w-full bg-white/5 rounded" />
                  <div className="h-4 w-1/2 bg-white/5 rounded" />
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Error */}
      {err && (
        <div className="card bg-red-500/10 border-red-500/20 text-red-200">
          {err}
        </div>
      )}

      {/* List */}
      {!loading && !err && (
        events.length === 0 ? (
          <section className="card text-slate-300">
            HLT in-house tickets will appear here when the ticketing API is available.
          </section>
        ) : (
          <section className="grid gap-4 sm:grid-cols-2">
            {events.map((ev) => {
              const badge = badgeFor(ev);
              const start = ev.date ? toDate(ev.date, ev.startTime) : null;
              const end = ev.date ? toDate(ev.date, ev.endTime) : null;

              // Hide prices & remaining when Coming soon
              const showSalesInfo = !isPlanned(ev);

              // Inventory & “you have” (only shown when not planned)
              const stdRem = ev.remainingStandardTickets || 0;
              const vipRem = ev.remainingVipTickets || 0;
              const vipRes = ev.reservedVipTickets || 0;
              const youHave = userTickets[ev.id] || 0;

              // Prices with fallbacks
              const stdPrice = ev.standardPrice ?? ev.standardTicketPrice;
              const vipPrice = ev.vipPrice ?? ev.vipTicketPrice;

              return (
                <article key={ev.id} className="card">
                  {/* Mobile-first vertical layout; image on top, side-by-side on md+ */}
                  <div className="grid gap-3 md:grid-cols-[7rem,1fr] md:items-start">
                    {/* Poster */}
                    <div className="relative">
                      {ev.imageUrl ? (
                        <SmartImg
                          src={ev.imageUrl.startsWith("http") ? ev.imageUrl : `${API_BASE}${ev.imageUrl}`}
                          alt={ev.title}
                          className="w-full aspect-[4/3] md:w-28 md:h-28 md:aspect-square rounded-xl object-cover border border-white/10"
                          width={448}
                          height={336}
                        />
                      ) : (
                        <div className="h-40 w-full md:h-28 md:w-28 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-[10px] text-slate-400">
                          No image
                        </div>
                      )}

                      {/* Status badge overlays image on mobile; sits inline on md+ */}
                      {badge && (
                        <span className={`badge absolute left-2 top-2 md:hidden ${badge.cls}`}>
                          {badge.label}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h2 className="truncate font-semibold text-white text-base sm:text-lg">
                          {ev.title}
                        </h2>
                        {/* Duplicate badge shown inline on md+ when overlay is static */}
                        <span className="hidden md:block">
                          {badge && (
                            <span className={`badge ${badge.cls}`}>{badge.label}</span>
                          )}
                        </span>
                      </div>

                      {ev.description && (
                        <p className="mt-1 text-sm text-slate-300">
                          {ev.description}
                        </p>
                      )}

                      {/* Meta */}
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-300">
                        <span className="badge">
                          {start
                            ? start.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })
                            : "Date TBC"}
                        </span>
                        <span className="badge">
                          {start && end
                            ? `${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                            : "Time TBC"}
                        </span>
                        <span className="badge max-w-[14rem] truncate" title={ev.location || "Location TBC"}>
                          {ev.location || "Location TBC"}
                        </span>
                      </div>

                      {/* Inventory + ownership (hidden for Coming soon) */}
                      {showSalesInfo && (
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          {(() => {
                            const s = stockPill(stdRem);
                            return <div className={`badge ${s.cls}`}>Standard: {s.text}</div>;
                          })()}
                          {(() => { const s = stockPill(vipRem, vipRes); return <div className={`badge ${s.cls}`}>VIP: {s.text}</div>; })()}
                          {youHave > 0 && (
                            <div className="col-span-2 badge bg-blue-500/15 text-blue-200">
                              You have {youHave} ticket{youHave > 1 ? "s" : ""}
                            </div>
                          )}
                        </div>
                      )}

                      {/* BIG Prices (hidden for Coming soon) */}
                      {showSalesInfo && (
                        <div className="mt-4 sm:mt-5 flex flex-wrap items-end justify-between gap-4">
                          <div className="flex flex-wrap gap-6">
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-slate-400">
                                Standard
                              </div>
                              <div className="font-display text-2xl md:text-3xl text-white leading-none">
                                {stdPrice != null ? fmtGBP(Number(stdPrice)) : "TBC"}
                              </div>
                            </div>
                            <div className="h-10 w-px bg-white/10 hidden sm:block" />
                            <div>
                              <div className="text-[11px] uppercase tracking-wider text-slate-400">
                                VIP
                              </div>
                              <div className="font-display text-2xl md:text-3xl text-white leading-none">
                                {vipPrice != null ? fmtGBP(Number(vipPrice)) : "TBC"}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action */}
                      <div className="mt-4">{actionFor(ev)}</div>
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        )
      )}
    </div>
  );
}
